import { isNotEmptyOrWhiteSpace } from "./strings";
import { parsePhoneNumber } from "react-phone-number-input";
import type { RuleObject } from "antd/es/form";

/**
 * Attempt to parse a value into a phone number.
 * @param phone The value to try parsing. Ideally some sort of string.
 * @returns A parsed phone number structure, or undefined if the number could not be parsed.
 */
export function parsePhone(phone: unknown) {
    if (!isNotEmptyOrWhiteSpace(phone)) {
        return undefined;
    }

    return parsePhoneNumber(phone, "US");
}

// Pre-compile regular expression, used to check valid phone numbers
const validPhoneRegex = new RegExp(/^\+1\d{10}$/);

/**
 * Check if supplied value is a complete North American phone number of the form: "+12125550100".
 * We could use libphonenumber-js to do this (ex. isPossiblePhoneNumber), however, as this library supports worldwide
 * phone numbers in various formats, checking for the exact form we expect is easier to reason about.
 */
export function isValidPhone(value: string) {
    return validPhoneRegex.test(value);
}

/**
 * Given a potentially invalid phone number from a PMS record, if the value can be interpreted as a partial or
 * too-long North American phone number, return the original value prefixed with "+1", so that it will render
 * within a react-phone-input configured for country "US".
 * Otherwise, return the original value unchanged.
 */
export function patchUpPhone(phone: string | undefined) {
    const parsed = parsePhone(phone);
    if (parsed?.countryCallingCode !== "1") {
        // return original if empty or completely invalid (ex. "abc"). react-phone-input will not render anything.
        // return original if valid or partial number from unexpected country (ex. "+44 000"). react-phone-input WILL render the value.
        return phone;
    }

    return parsed.number;
}

/** Validate a phone number as an AntD form item rule. */
export function phoneValidator(name = "Phone Number") {
    return (rule: RuleObject, value: string) => {
        if (!value) {
            if (rule.required) {
                return Promise.reject(new Error(`${name} is required`));
            } else {
                return Promise.resolve();
            }
        }

        if (!isValidPhone(value)) {
            return Promise.reject(new Error("Please enter a valid phone number"));
        }

        return Promise.resolve();
    };
}
